import type { OrgMember } from '@zhiyue/core';
import type { RelationOption } from '@/modules/zy-crm/utils/relation-options';
import { getCurrentMemberOption } from '@/utils/currentMember';
import { orgDirectoryAdapter } from '../utils/org-directory-adapter';
import { isResolvedMemberLabel } from './member-label';

export type MemberDirectoryState = {
  memberOptions: RelationOption[];
  memberNameMap: Record<string, string>;
  loading: boolean;
  loaded: boolean;
  error: Error | null;
};

export type MemberSearchOptions = {
  ensureValue?: string;
  forceRemote?: boolean;
  minLocalResults?: number;
};

export type MemberDirectorySnapshot = MemberDirectoryState & {
  ensureMembersLoaded: () => Promise<RelationOption[]>;
  refreshMembers: () => Promise<RelationOption[]>;
  searchMembers: (
    keyword?: string,
    options?: MemberSearchOptions,
  ) => Promise<RelationOption[]>;
  ensureMemberValue: (value?: string | null) => Promise<RelationOption[]>;
  subscribe: (listener: () => void) => () => void;
};

type NormalizedMember = Record<string, unknown>;

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_MIN_LOCAL_RESULTS = 8;
const MEMBER_VALUE_KEYS = [
  'memberUuid',
  'id',
  'uuid',
  'userUuid',
  'userId',
] as const;

const listeners = new Set<() => void>();

let state: MemberDirectoryState = {
  memberOptions: [],
  memberNameMap: {},
  loading: false,
  loaded: false,
  error: null,
};

let snapshot: MemberDirectoryState = state;
let externalSnapshot: MemberDirectorySnapshot;

let snapshotApi: Omit<MemberDirectorySnapshot, keyof MemberDirectoryState>;

let initialLoadPromise: Promise<RelationOption[]> | null = null;
const keywordLoadPromises = new Map<string, Promise<RelationOption[]>>();

const syncExternalSnapshot = () => {
  externalSnapshot = {
    ...snapshot,
    ...snapshotApi,
  };
};

const normalize = (value: unknown) => String(value ?? '').trim();

const normalizeOrgMemberForLookup = (
  member?: OrgMember | null,
): NormalizedMember | null => {
  if (!member) {
    return null;
  }
  const memberUuid = normalize(member.memberUuid);
  if (!memberUuid) {
    return null;
  }
  return {
    ...member,
    memberUuid,
    name: normalize(member.name) || normalize(member.nickname) || undefined,
  };
};

const notify = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setState = (next: Partial<MemberDirectoryState>) => {
  state = {
    ...state,
    ...next,
  };
  snapshot = state;
  syncExternalSnapshot();
  notify();
};

export const getMemberValueKeys = (record: Record<string, unknown>) =>
  Array.from(
    new Set(
      MEMBER_VALUE_KEYS.map((key) => normalize(record[key])).filter(Boolean),
    ),
  );

export const getPrimaryMemberValue = (record: Record<string, unknown>) =>
  getMemberValueKeys(record)[0] || '';

export const getMemberLabel = (record: Record<string, unknown>) => {
  const fallback = getPrimaryMemberValue(record);
  return (
    normalize(record.name) ||
    normalize(record.nickname) ||
    normalize(record.username) ||
    normalize(record.title) ||
    normalize(record.phone) ||
    fallback
  );
};

export const toMemberOptions = (
  list: Array<Record<string, unknown> | null | undefined>,
) => {
  const out: RelationOption[] = [];
  (list || []).forEach((item) => {
    if (!item) return;
    const value = getPrimaryMemberValue(item);
    if (!value) return;
    out.push({
      value,
      label: getMemberLabel(item),
      raw: item,
    });
  });
  const currentMemberOption = getCurrentMemberOption();
  if (currentMemberOption) {
    out.push(currentMemberOption);
  }
  return toUniqueOptions(out);
};

export const buildMemberNameMap = (
  list: Array<Record<string, unknown> | null | undefined>,
) => {
  const out: Record<string, string> = {};
  (list || []).forEach((item) => {
    if (!item) return;
    const label = getMemberLabel(item);
    if (!label) return;
    getMemberValueKeys(item).forEach((key) => {
      out[key] = label;
    });
  });
  const currentMemberOption = getCurrentMemberOption();
  if (currentMemberOption) {
    out[currentMemberOption.value] = currentMemberOption.label;
  }
  return out;
};

export const mergeOptions = (
  prev: RelationOption[],
  next: RelationOption[],
  ensureValue?: string,
): RelationOption[] => {
  const map = new Map<string, RelationOption>();
  prev.forEach((item) => {
    if (item.value) {
      map.set(item.value, item);
    }
  });
  next.forEach((item) => {
    if (item.value) {
      map.set(item.value, item);
    }
  });
  const safeEnsure = normalize(ensureValue);
  if (safeEnsure && !map.has(safeEnsure)) {
    map.set(safeEnsure, { value: safeEnsure, label: safeEnsure });
  }
  return Array.from(map.values());
};

export const toUniqueOptions = (items: RelationOption[]) => {
  const map = new Map<string, RelationOption>();
  items.forEach((item) => {
    if (item.value) {
      map.set(item.value, item);
    }
  });
  return Array.from(map.values());
};

export const mergeMemberDirectory = (
  prev: MemberDirectoryState,
  list: Array<Record<string, unknown> | null | undefined>,
  ensureValue?: string,
): MemberDirectoryState => {
  const nextOptions = toMemberOptions(list);
  const nextNameMap = buildMemberNameMap(list);
  return {
    ...prev,
    memberOptions: mergeOptions(prev.memberOptions, nextOptions, ensureValue),
    memberNameMap: {
      ...prev.memberNameMap,
      ...nextNameMap,
    },
    loaded: true,
    loading: false,
    error: null,
  };
};

const loadMemberList = async (keyword?: string) => {
  const result = await orgDirectoryAdapter.searchMembers({
    keyword: keyword || undefined,
    pageNo: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  return (result?.list || [])
    .map((item) => normalizeOrgMemberForLookup(item))
    .filter(Boolean) as NormalizedMember[];
};

const updateFromRemote = async (keyword?: string, ensureValue?: string) => {
  const list = await loadMemberList(keyword);
  state = mergeMemberDirectory(
    {
      ...state,
      loading: false,
    },
    list,
    ensureValue,
  );
  snapshot = state;
  syncExternalSnapshot();
  notify();
  return state.memberOptions;
};

const runInitialLoad = async (force = false) => {
  if (state.loaded && !force) {
    return state.memberOptions;
  }
  if (initialLoadPromise && !force) {
    return initialLoadPromise;
  }
  setState({
    loading: true,
    error: null,
  });
  const promise = updateFromRemote()
    .catch((error) => {
      setState({
        loading: false,
        error: error instanceof Error ? error : new Error('成员列表加载失败'),
      });
      throw error;
    })
    .finally(() => {
      initialLoadPromise = null;
    });
  initialLoadPromise = promise;
  return promise;
};

const runKeywordLoad = async (keyword: string, ensureValue?: string) => {
  const cacheKey = `${keyword}::${ensureValue || ''}`;
  const existing = keywordLoadPromises.get(cacheKey);
  if (existing) {
    return existing;
  }
  const promise = updateFromRemote(keyword, ensureValue)
    .catch((error) => {
      setState({
        error: error instanceof Error ? error : new Error('成员列表加载失败'),
      });
      throw error;
    })
    .finally(() => {
      keywordLoadPromises.delete(cacheKey);
    });
  keywordLoadPromises.set(cacheKey, promise);
  return promise;
};

const filterLocalMembers = (keyword?: string, ensureValue?: string) => {
  const normalizedKeyword = normalize(keyword).toLowerCase();
  const filtered = !normalizedKeyword
    ? state.memberOptions
    : state.memberOptions.filter((item) => {
        const value = normalize(item.value).toLowerCase();
        const label = normalize(item.label).toLowerCase();
        return (
          value.includes(normalizedKeyword) || label.includes(normalizedKeyword)
        );
      });
  return mergeOptions(filtered, [], ensureValue);
};

const ensureMemberValue = async (value?: string | null) => {
  const safeValue = normalize(value);
  if (!safeValue) {
    return state.memberOptions;
  }
  const mappedName = normalize(state.memberNameMap[safeValue]);
  const optionLabel = normalize(
    state.memberOptions.find((item) => item.value === safeValue)?.label,
  );
  const hasResolvedName =
    isResolvedMemberLabel(safeValue, mappedName) ||
    isResolvedMemberLabel(safeValue, optionLabel);
  if (hasResolvedName) {
    return mergeOptions(state.memberOptions, [], safeValue);
  }
  if (orgDirectoryAdapter.getMemberById) {
    const member = await orgDirectoryAdapter.getMemberById(safeValue);
    const normalizedMember = normalizeOrgMemberForLookup(member);
    if (normalizedMember) {
      state = mergeMemberDirectory(
        {
          ...state,
          loading: false,
        },
        [normalizedMember],
        safeValue,
      );
      snapshot = state;
      syncExternalSnapshot();
      notify();
      return mergeOptions(state.memberOptions, [], safeValue);
    }
  }
  return runKeywordLoad(safeValue, safeValue);
};

snapshotApi = {
  ensureMembersLoaded: () => runInitialLoad(false),
  refreshMembers: () => runInitialLoad(true),
  searchMembers: async (keyword?: string, options?: MemberSearchOptions) => {
    const safeKeyword = normalize(keyword);
    const ensureValue = normalize(options?.ensureValue);
    const localResults = filterLocalMembers(safeKeyword, ensureValue);
    const minLocalResults =
      options?.minLocalResults ?? DEFAULT_MIN_LOCAL_RESULTS;
    const shouldFetchRemote =
      options?.forceRemote ||
      (!state.loaded && !state.loading) ||
      (safeKeyword !== '' && localResults.length < minLocalResults);

    if (!shouldFetchRemote) {
      return localResults;
    }

    if (!safeKeyword && !options?.forceRemote) {
      return runInitialLoad(false);
    }

    await runKeywordLoad(safeKeyword, ensureValue);
    return filterLocalMembers(safeKeyword, ensureValue);
  },
  ensureMemberValue,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

syncExternalSnapshot();

export const memberDirectoryStore = snapshotApi;

export const getMemberDirectorySnapshot = () => externalSnapshot;

export const clearMemberDirectoryStore = () => {
  state = {
    memberOptions: [],
    memberNameMap: {},
    loading: false,
    loaded: false,
    error: null,
  };
  snapshot = state;
  syncExternalSnapshot();
  initialLoadPromise = null;
  keywordLoadPromises.clear();
  notify();
};

export const resetMemberDirectoryStoreForTest = () => {
  clearMemberDirectoryStore();
  listeners.clear();
};
